/**
 * Groups `seller-service-price` rows into the `Category → sub1 → sub2` tree every service-price
 * view renders. Pure and framework-free; each view supplies its own node fields via builders.
 *
 * Each row prices a service and names its position through three relationships, each carrying a
 * stable resource id and resolved to a display title against the document's `included` resources:
 *   - `category` always → the category the row belongs to.
 *   - `sub1Service` → the sub1 service, or empty `data` when the row prices the whole category.
 *   - `sub2Detail` → the sub2 detail, or empty `data` when the form asks no detail.
 *
 * So a row lands at the deepest level its relationships describe:
 *   - category only (no `sub1Service`) → prices the whole category (rendered as a single row
 *     labeled with the category title, since the UI has no category-level price slot).
 *   - category and service → the sub1 row carries the price.
 *   - category + service + detail → the sub2 row carries the price, nested under its sub1.
 *
 * Grouping is keyed by the relationship **id** (so services with the same title stay distinct),
 * with the resolved title used only as the display label. The ids exposed to the UI are
 * synthesized from those real relationship ids (DOM-safe) purely for React keys / `Collapsible`
 * ids — never ordinal counters, so ids stay stable across reorders and refetches:
 *   - category → `cat-{id}`.
 *   - sub1 service → `svc-{id}` for a standard `choice-value`, `custom-svc-{id}` for a
 *     `custom-service` (the type prefix keeps ids from colliding, since ids are only unique
 *     within a resource type).
 *   - sub2 detail → `dtl-{id}` for a standard `choice-value`, `custom-dtl-{id}` for a
 *     `custom-detail`.
 * Row insertion order is preserved throughout.
 */

import {
  SellerServicePriceAttributes,
  ServiceLevel,
  ServicePrice,
  ServicePriceIncludedResource,
  ServicePriceRelationship,
  ServicePriceRowRelationships
} from './types';

const toNumber = (value?: string | null): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

/** The price fields of a row; `amount` is `null` on a settings row the pro has not priced yet. */
type RawPriceFields = Pick<SellerServicePriceAttributes, 'priceType' | 'priceUnit' | 'amountMax'> & {
  amount?: string | null;
};

export const mapPrice = (attrs: RawPriceFields): ServicePrice => ({
  priceType: attrs.priceType,
  priceUnit: attrs.priceUnit,
  // NaN when unparseable — the formatter guards and renders the row label-only.
  amount: toNumber(attrs.amount) ?? Number.NaN,
  amountMax: toNumber(attrs.amountMax)
});

/** Indexes sideloaded label resources by `type:id` for O(1) relationship resolution. */
export const indexIncluded = (included: Array<ServicePriceIncludedResource> = []): Map<string, ServicePriceIncludedResource> => {
  const resourceByRef = new Map<string, ServicePriceIncludedResource>();
  for (const resource of included) {
    resourceByRef.set(`${resource.type}:${resource.id}`, resource);
  }

  return resourceByRef;
};

/**
 * A relationship resolved to its stable resource `type`/`id` and its sideloaded display `title`.
 * `resource` is the sideloaded record itself, absent when the linked record was not sideloaded.
 */
export interface ResolvedRef {
  type: string;
  id: string;
  title: string;
  resource?: ServicePriceIncludedResource;
}

/** Resolves a relationship to its `{ type, id, title, resource }`, or `undefined` when the link is empty. */
export const resolveRef = (
  resourceByRef: Map<string, ServicePriceIncludedResource>,
  relationship?: ServicePriceRelationship
): ResolvedRef | undefined => {
  const linkage = relationship?.data;
  if (!linkage) {
    return undefined;
  }
  const resource = resourceByRef.get(`${linkage.type}:${linkage.id}`);

  return {
    type: linkage.type,
    id: linkage.id,
    title: resource?.attributes.title ?? '',
    resource
  };
};

/** Resource-type → id prefix for a sub1 service row; `custom-service` gets a distinct prefix. */
const SUB1_ID_PREFIX: Record<string, string> = {
  'choice-value': 'svc',
  'custom-service': 'custom-svc'
};

/** Resource-type → id prefix for a sub2 detail row; `custom-detail` gets a distinct prefix. */
const SUB2_ID_PREFIX: Record<string, string> = {
  'choice-value': 'dtl',
  'custom-detail': 'custom-dtl'
};

/** DOM-safe id for a category, keyed by its real relationship id. */
const categoryTreeId = (category: ResolvedRef): string => `cat-${category.id}`;

/** DOM-safe id for a sub1 service — type-prefixed so cross-type ids never collide. */
export const sub1TreeId = (ref: Pick<ResolvedRef, 'type' | 'id'>): string => `${SUB1_ID_PREFIX[ref.type] ?? 'svc'}-${ref.id}`;

/** DOM-safe id for a sub2 detail — type-prefixed so cross-type ids never collide. */
const sub2TreeId = (ref: ResolvedRef): string => `${SUB2_ID_PREFIX[ref.type] ?? 'dtl'}-${ref.id}`;

/** A price row as the tree builder reads it: only the relationships that place it. */
export interface ServiceTreeRow {
  relationships?: ServicePriceRowRelationships;
}

/** The fields every service node shares, whichever view renders it. */
export interface ServiceTreeNode {
  serviceId: string;
  level: ServiceLevel;
  label: string;
}

/** Where a node sits: its synthesized ids plus the resolved refs above and at it. */
export interface ServiceNodePosition extends ServiceTreeNode {
  categoryId: string;
  category: ResolvedRef;
  sub1?: ResolvedRef;
  sub2?: ResolvedRef;
}

export interface ServiceTreeBuilders<TRow extends ServiceTreeRow, TService extends ServiceTreeNode, TCategory> {
  /** A node with no price row of its own yet. */
  createNode(position: ServiceNodePosition): TService;
  /** Puts a row's price fields onto the node it prices. */
  applyRow(node: TService, row: TRow): TService;
  createCategory(category: ResolvedRef, categoryId: string, services: Array<TService>): TCategory;
}

/** An ordered category bucket: its resolved ref, its sub1 services and their sub2 children. */
interface CategoryBucket<TService> {
  category: ResolvedRef;
  services: Map<string, TService>;
  childrenBySub1: Map<string, Array<TService>>;
}

/**
 * Groups price rows into the ordered `Category → sub1 → sub2` tree, keyed by relationship id.
 * Row insertion order is preserved. A sub2 row whose sub1 has no row of its own still gets
 * its sub1 node, via `createNode` only. `seedCategories` open buckets up front, in order, so
 * categories without rows are kept.
 */
export const buildServiceTree = <TRow extends ServiceTreeRow, TService extends ServiceTreeNode, TCategory>(
  rows: Array<TRow>,
  resourceByRef: Map<string, ServicePriceIncludedResource>,
  builders: ServiceTreeBuilders<TRow, TService, TCategory>,
  seedCategories: Array<ResolvedRef> = []
): Array<TCategory> => {
  const buckets = new Map<string, CategoryBucket<TService>>();

  const ensureCategory = (category: ResolvedRef): CategoryBucket<TService> => {
    const categoryId = categoryTreeId(category);
    const existing = buckets.get(categoryId);
    if (existing) {
      return existing;
    }
    const created: CategoryBucket<TService> = { category, services: new Map(), childrenBySub1: new Map() };
    buckets.set(categoryId, created);

    return created;
  };

  seedCategories.forEach(ensureCategory);

  for (const row of rows) {
    const relationships = row.relationships;
    const category = resolveRef(resourceByRef, relationships?.category);
    if (!category) {
      // The category link is present by contract — skip a row that lacks it rather than crash.
      continue;
    }

    const bucket = ensureCategory(category);
    const categoryId = categoryTreeId(category);
    const sub1Ref = resolveRef(resourceByRef, relationships?.sub1Service);
    const sub2Ref = resolveRef(resourceByRef, relationships?.sub2Detail);

    // Whole-category price: a single sub1 row labeled with the category title, id derived from the category
    const sub1Position: ServiceNodePosition = sub1Ref
      ? { serviceId: sub1TreeId(sub1Ref), level: 'sub1', label: sub1Ref.title, categoryId, category, sub1: sub1Ref }
      : { serviceId: `${categoryId}-whole`, level: 'sub1', label: category.title, categoryId, category };
    const sub1 = bucket.services.get(sub1Position.serviceId) ?? builders.createNode(sub1Position);

    if (!sub1Ref || !sub2Ref) {
      bucket.services.set(sub1Position.serviceId, builders.applyRow(sub1, row));
      continue;
    }

    bucket.services.set(sub1Position.serviceId, sub1);
    const child = builders.applyRow(
      builders.createNode({
        serviceId: sub2TreeId(sub2Ref),
        level: 'sub2',
        label: sub2Ref.title,
        categoryId,
        category,
        sub1: sub1Ref,
        sub2: sub2Ref
      }),
      row
    );
    bucket.childrenBySub1.set(sub1Position.serviceId, [...(bucket.childrenBySub1.get(sub1Position.serviceId) ?? []), child]);
  }

  return Array.from(buckets.entries())
    .map(([categoryId, bucket]) => {
      const services = Array.from(bucket.services.values())
        .map(service => {
          const children = bucket.childrenBySub1.get(service.serviceId);

          return children ? { ...service, children } : service;
        });

      return builders.createCategory(bucket.category, categoryId, services);
    });
};
